import { useRef, useState } from 'react';

import { Alert, Button, TextField } from '@/ui';

import type { MomentsArtifactSurveyQuestionId } from '@/store/api/momentsApi';
import type { CSSProperties } from 'react';

export const ARTIFACT_SURVEY_MAX_TEXT_LENGTH = 1000;
// How long a clicked answer stays filled before the next question replaces it.
export const ARTIFACT_SURVEY_ADVANCE_DELAY_MS = 500;
export const ARTIFACT_SURVEY_SAVE_ERROR = "Couldn't save that. Try again.";
export const ARTIFACT_SURVEY_THANKS = 'Thanks, this helps us get it right.';

type ChoiceQuestionId = Exclude<MomentsArtifactSurveyQuestionId, 'stood_out'>;

interface ChoiceQuestion {
  choices: Array<{ label: string; value: string }>;
  id: ChoiceQuestionId;
  text: string;
}

const CHOICE_QUESTIONS: ChoiceQuestion[] = [
  {
    choices: [
      { label: 'Yes', value: 'yes' },
      { label: 'Not really', value: 'not_really' },
    ],
    id: 'got_you',
    text: 'Did this get you?',
  },
  {
    choices: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    id: 'will_try',
    text: "Anything here you'll actually try?",
  },
];

const FREE_TEXT_STEP = CHOICE_QUESTIONS.length;
const THANKS_STEP = FREE_TEXT_STEP + 1;
const QUESTION_COUNT = CHOICE_QUESTIONS.length + 1;

const containerStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginTop: 32,
};

const dotsStyles: CSSProperties = {
  display: 'flex',
  gap: 6,
};

const dotStyles = (active: boolean): CSSProperties => ({
  backgroundColor: active ? '#637A6F' : '#E3E3E3',
  borderRadius: '50%',
  height: 6,
  width: 6,
});

const questionStyles: CSSProperties = {
  color: '#262626',
  fontFamily: "'Basis Grotesque', sans-serif",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: 1.5,
  margin: 0,
};

const choicesStyles: CSSProperties = {
  display: 'flex',
  gap: 8,
};

const thanksStyles: CSSProperties = {
  ...questionStyles,
  color: '#5C5C5C',
  fontWeight: 400,
};

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export interface ArtifactSurveyProps {
  // Resolves once the answer is saved; rejects when the save fails.
  onAnswer: (
    question: MomentsArtifactSurveyQuestionId,
    answer: string,
  ) => Promise<void>;
}

export const ArtifactSurvey = ({ onAnswer }: ArtifactSurveyProps) => {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  // Guards against a second click landing before the saving render.
  const isSavingRef = useRef(false);

  const handleChoice = async (question: ChoiceQuestion, value: string) => {
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    setSelected(value);
    setError(null);
    setIsSaving(true);
    try {
      await Promise.all([
        onAnswer(question.id, value),
        wait(ARTIFACT_SURVEY_ADVANCE_DELAY_MS),
      ]);
      setSelected(null);
      setStep((current) => current + 1);
    } catch {
      setError(ARTIFACT_SURVEY_SAVE_ERROR);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDone = async () => {
    if (isSavingRef.current) {
      return;
    }
    const trimmed = text.trim();
    if (!trimmed) {
      setStep(THANKS_STEP);
      return;
    }
    isSavingRef.current = true;
    setError(null);
    setIsSaving(true);
    try {
      await onAnswer('stood_out', trimmed);
      setStep(THANKS_STEP);
    } catch {
      setError(ARTIFACT_SURVEY_SAVE_ERROR);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  if (step === THANKS_STEP) {
    return (
      <section aria-label="Artifact survey" style={containerStyles}>
        <p style={thanksStyles}>{ARTIFACT_SURVEY_THANKS}</p>
      </section>
    );
  }

  const choiceQuestion = CHOICE_QUESTIONS[step];

  return (
    <section aria-label="Artifact survey" style={containerStyles}>
      <div aria-hidden="true" style={dotsStyles}>
        {Array.from({ length: QUESTION_COUNT }, (_, index) => (
          <span key={index} style={dotStyles(index <= step)} />
        ))}
      </div>
      {choiceQuestion ? (
        <>
          <p style={questionStyles}>{choiceQuestion.text}</p>
          <div style={choicesStyles}>
            {choiceQuestion.choices.map((choice) => (
              <Button
                key={choice.value}
                aria-pressed={selected === choice.value}
                color="primary"
                variant={selected === choice.value ? 'contained' : 'outlined'}
                onClick={() => handleChoice(choiceQuestion, choice.value)}
              >
                {choice.label}
              </Button>
            ))}
          </div>
        </>
      ) : (
        <>
          <p style={questionStyles}>What stood out?</p>
          <TextField
            disabled={isSaving}
            inputProps={{
              'aria-label': 'What stood out?',
              maxLength: ARTIFACT_SURVEY_MAX_TEXT_LENGTH,
            }}
            maxRows={8}
            minRows={3}
            placeholder="Optional"
            value={text}
            fullWidth
            multiline
            onChange={(event) => setText(event.target.value)}
          />
          <div>
            <Button
              color="primary"
              disabled={isSaving}
              variant="contained"
              onClick={handleDone}
            >
              Done
            </Button>
          </div>
        </>
      )}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </section>
  );
};
